import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Handbag, MapPin, Clock, Star, Phone, WhatsappLogo, Diamond, Storefront, ShoppingCart } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";

const whatsappMsg = encodeURIComponent("Bonjour, je souhaite organiser une journée shopping luxe à Paris avec chauffeur privé ALTURA. Pouvez-vous me faire un devis ?");
const whatsappUrl = `https://wa.me/33681969342?text=${whatsappMsg}`;

const ShoppingLuxePage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Shopping Luxe Paris avec Chauffeur Privé — ALTURA",
    "description": "Journée shopping luxe à Paris avec chauffeur privé. Avenue Montaigne, Faubourg Saint-Honoré, Place Vendôme, Le Bon Marché. Service personnalisé.",
    "url": "https://alturaconciergerie.com/experiences/shopping-luxe",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ALTURA Conciergerie",
      "telephone": "+33681969342",
      "address": { "@type": "PostalAddress", "addressLocality": "Paris", "addressCountry": "FR" },
    },
  };

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="shopping-luxe-page">
      <Helmet>
        <title>Shopping Luxe Paris avec Chauffeur Privé | Montaigne, Vendôme | ALTURA</title>
        <meta name="description" content="Journée shopping luxe à Paris avec chauffeur privé ALTURA. Avenue Montaigne, Place Vendôme, Faubourg Saint-Honoré, Le Bon Marché. Transport et discrétion." />
        <link rel="canonical" href="https://alturaconciergerie.com/experiences/shopping-luxe" />
        <meta property="og:title" content="Shopping Luxe Paris — Chauffeur Privé | ALTURA" />
        <meta property="og:description" content="Les plus belles adresses du luxe parisien avec votre chauffeur privé dédié." />
        <meta property="og:url" content="https://alturaconciergerie.com/experiences/shopping-luxe" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.pexels.com/photos/19789287/pexels-photo-19789287.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[450px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/19789287/pexels-photo-19789287.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Place Vendôme Paris — Shopping luxe ALTURA"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EDEBE4] via-[#EDEBE4]/40 to-[#1A1A1A]/20" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-12 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/70 mb-3">ALTURA · Expériences</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight">
                Shopping luxe à Paris
              </h1>
              <p className="text-[#1A1A1A]/70 text-base mt-4 max-w-xl font-light">
                Les plus belles adresses du luxe parisien, un chauffeur dédié à votre service.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-6">L'art du shopping parisien</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-6">
                Paris est la capitale mondiale du luxe. De l'Avenue Montaigne à la Place Vendôme, du Faubourg Saint-Honoré au Triangle d'Or, les plus grandes maisons de mode, joaillerie et horlogerie vous ouvrent leurs portes. Avec ALTURA, vivez cette expérience dans un confort absolu.
              </p>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">
                Votre chauffeur vous attend devant chaque boutique, gère vos achats dans le véhicule et vous transporte d'adresse en adresse sans contrainte. Pas de stationnement à chercher, pas de sacs à porter — concentrez-vous sur l'essentiel : le plaisir du shopping.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="shopping-cta-whatsapp">
                  <WhatsappLogo size={18} weight="fill" /> Devis shopping
                </a>
                <Link to="/contact" className="border border-[#1A1A1A] text-[#1A1A1A] px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#1A1A1A] hover:text-[#EDEBE4] transition-colors inline-flex items-center gap-2" data-testid="shopping-cta-contact">
                  <Phone size={16} /> Nous contacter
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <img
                src="https://images.unsplash.com/photo-1748191638705-307c324fa948?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Boutiques de luxe Paris — ALTURA"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quartiers du luxe */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">Les adresses incontournables</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A]">Les quartiers du luxe parisien</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              {
                icon: <Diamond size={28} weight="thin" />,
                title: "Place Vendôme",
                desc: "Le temple de la haute joaillerie. Cartier, Van Cleef & Arpels, Boucheron, Chaumet, Bulgari. L'écrin le plus prestigieux de Paris pour les bijoux et montres d'exception.",
                addresses: "Cartier, Van Cleef & Arpels, Boucheron, Chaumet, Piaget",
              },
              {
                icon: <Storefront size={28} weight="thin" />,
                title: "Avenue Montaigne",
                desc: "Le Triangle d'Or de la mode. Dior, Chanel, Louis Vuitton, Saint Laurent, Valentino, Fendi. Les flagship stores des plus grandes maisons dans un cadre architectural somptueux.",
                addresses: "Dior, Chanel, Louis Vuitton, Saint Laurent, Valentino",
              },
              {
                icon: <Handbag size={28} weight="thin" />,
                title: "Faubourg Saint-Honoré",
                desc: "La rue la plus élégante de Paris. Hermès (au 24, la maison mère), Lanvin, Goyard, Astier de Villatte. Un héritage de luxe depuis le XVIIIe siècle.",
                addresses: "Hermès, Lanvin, Goyard, Astier de Villatte, Colette",
              },
              {
                icon: <ShoppingCart size={28} weight="thin" />,
                title: "Grands Magasins & Le Marais",
                desc: "Le Bon Marché (rive gauche), les Galeries Lafayette et le Printemps (rive droite). Le Marais pour les concept stores et créateurs indépendants.",
                addresses: "Le Bon Marché, Galeries Lafayette, Merci, The Broken Arm",
              },
            ].map((quartier, i) => (
              <motion.div
                key={quartier.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#EDEBE4] border border-[#1A1A1A]/10 p-6"
              >
                <div className="text-[#1A1A1A] mb-4">{quartier.icon}</div>
                <h3 className="font-heading text-lg text-[#1A1A1A] mb-2">{quartier.title}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed mb-3">{quartier.desc}</p>
                <p className="text-[#1A1A1A]/50 text-xs">{quartier.addresses}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme type */}
      <section className="py-16 sm:py-20 bg-[#EDEBE4]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-8">Exemple de journée shopping luxe</h2>
            <div className="space-y-6">
              {[
                { time: "10h00", title: "Prise en charge", desc: "Votre chauffeur vous accueille à votre hôtel. Direction le Triangle d'Or." },
                { time: "10h30", title: "Avenue Montaigne", desc: "Dior, Chanel, Louis Vuitton, Valentino. Vos achats sont déposés au fur et à mesure dans le véhicule." },
                { time: "12h30", title: "Déjeuner", desc: "Pause déjeuner au restaurant L'Avenue ou au Plaza Athénée. Votre chauffeur vous dépose et vous attend." },
                { time: "14h00", title: "Place Vendôme", desc: "Haute joaillerie : Cartier, Van Cleef & Arpels, Boucheron. Le temps s'arrête dans les salons privés." },
                { time: "16h00", title: "Faubourg Saint-Honoré", desc: "Hermès, Goyard, Lanvin. La rue la plus chic de Paris, boutique après boutique." },
                { time: "17h30", title: "Le Marais ou Le Bon Marché", desc: "Concept stores tendance ou le grand magasin le plus raffiné de Paris. Selon vos envies." },
                { time: "19h00", title: "Retour", desc: "Retour à l'hôtel avec tous vos achats. Votre chauffeur vous aide à tout transporter." },
              ].map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex gap-6 items-start"
                >
                  <div className="w-16 flex-shrink-0 text-right">
                    <p className="text-[#1A1A1A] text-sm font-medium">{step.time}</p>
                  </div>
                  <div className="w-px bg-[#1A1A1A]/20 min-h-[60px] flex-shrink-0 relative">
                    <div className="absolute top-1 -left-[3px] w-[7px] h-[7px] bg-[#1A1A1A] rounded-full" />
                  </div>
                  <div className="flex-1 pb-4">
                    <h3 className="text-[#1A1A1A] font-medium text-sm mb-1">{step.title}</h3>
                    <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services inclus */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A]">Nos services shopping</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: <Star size={28} weight="thin" />, title: "Personal Shopping", desc: "Sur demande, nous organisons des rendez-vous privés dans les boutiques pour une expérience exclusive." },
              { icon: <MapPin size={28} weight="thin" />, title: "Itinéraire optimisé", desc: "Votre chauffeur organise le parcours pour maximiser votre temps et minimiser les trajets." },
              { icon: <Clock size={28} weight="thin" />, title: "Aucune limite de temps", desc: "Prenez votre temps dans chaque boutique. Votre chauffeur vous attend devant, prêt à partir." },
            ].map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#EDEBE4] border border-[#1A1A1A]/10 p-6 text-center"
              >
                <div className="text-[#1A1A1A] mb-4 flex justify-center">{service.icon}</div>
                <h3 className="font-heading text-lg text-[#1A1A1A] mb-2">{service.title}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-[#EDEBE4]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-10">Questions fréquentes</h2>
          <div className="space-y-6">
            {[
              { q: "Combien coûte une journée shopping avec chauffeur ?", a: "Le tarif dépend du véhicule et de la durée de mise à disposition. Nos formules commencent à la demi-journée (4h). Contactez-nous pour un devis personnalisé." },
              { q: "Le chauffeur peut-il m'aider avec mes achats ?", a: "Bien sûr. Votre chauffeur vous aide à charger vos achats dans le véhicule et veille sur eux pendant que vous continuez votre shopping." },
              { q: "Pouvez-vous organiser des rendez-vous privés en boutique ?", a: "Oui, nous pouvons contacter les boutiques en amont pour organiser des rendez-vous privés et des expériences VIP dans les maisons de luxe." },
              { q: "Est-ce adapté pour un groupe ?", a: "Absolument. Pour les groupes (amies, famille, clients d'entreprise), nous proposons la Mercedes Classe V 7 places ou plusieurs véhicules selon la taille du groupe." },
            ].map((item, i) => (
              <motion.div key={item.q} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h3 className="text-[#1A1A1A] font-medium text-sm mb-2">{item.q}</h3>
                <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#EDEBE4] mb-4">Le luxe parisien vous attend</h2>
          <p className="text-[#EDEBE4]/60 text-sm mb-8">Organisez votre journée shopping sur mesure avec un chauffeur privé ALTURA.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="shopping-cta-bottom-wa">
              <WhatsappLogo size={18} weight="fill" /> Devis WhatsApp
            </a>
            <a href="tel:+33681969342" className="bg-[#EDEBE4] text-[#1A1A1A] px-10 py-4 text-sm tracking-wider uppercase hover:bg-white transition-colors inline-flex items-center gap-2" data-testid="shopping-cta-bottom-call">
              <Phone size={16} /> +33 6 81 96 93 42
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ShoppingLuxePage;
